/**
 * Auth bridge consumer for StudentPortal.
 *
 * MUST be invoked synchronously before ReactDOM.render(), because
 * <Authentication> calls history.replace("/") during render — which would
 * strip any auth params from the URL hash fallback before useEffects fire.
 *
 * Transport priority:
 *   1. First-party cookie (_cy_auth_bridge) on shared parent domain — primary
 *   2. URL hash param (_spb) — fallback for localhost cross-port dev only
 *
 * On success: writes localStorage.user (the existing SP auth store) using
 * the same encrypt() wrapper SP already uses, then deletes the cookie and
 * cleans the URL.
 *
 * Always wrapped in try/catch — must never throw and break app bootstrap.
 */

import { decrypt } from "./other";
import { getCookie, deleteCookie } from "./cookieUtils";
import { saveSession } from "./saveSession";
import { rememberSandboxReturn, forgetSandboxReturn } from "./returnToSandbox";

const BRIDGE_COOKIE_NAME = "_cy_auth_bridge";
const BRIDGE_TTL_MS = 30 * 1000;

/**
 * Writes the bridged user object into localStorage.user using SP's own
 * encrypt(). The shape comes from Sandbox already pre-mapped to match
 * SP's /login/student response (studentName, studentEmail, profilePicUrl,
 * source, ACCESS_TOKEN, REFRESH_TOKEN, msg, ...) — see
 * sandbox/src/Core/utils/redirectToStudentPortal.js → buildSpUserObject.
 */
// Session keys to clear before writing the bridged user, so an impersonation /
// account switch can't leave a previous account's state behind. These are
// re-populated below from the new payload where applicable; the embedded
// Sandbox MFE keys (sandbox_user*) are not re-set here and must not linger.
const STALE_SESSION_KEYS = [
  "user",
  "cy-user",
  "profile_pic",
  "sandbox_user",
  "sandbox_user_data",
  "studentId",
];

const writeUser = (user) => {
  // Clear any previous account's state first (impersonation / account switch),
  // including the embedded Sandbox MFE keys which must not linger.
  STALE_SESSION_KEYS.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  });

  // Persist through the common saveSession — writes localStorage.user, the
  // cy-user checkout key, profile_pic, AND the shared cross-app cookies + sync
  // flag, so the bridged session is picked up consistently everywhere.
  saveSession({
    accessToken: user.ACCESS_TOKEN,
    refreshToken: user.REFRESH_TOKEN,
    userData: user,
  });
};

const isValidPayload = (p) =>
  p &&
  typeof p === "object" &&
  typeof p.ts === "number" &&
  p.user &&
  typeof p.user === "object" &&
  typeof p.user.ACCESS_TOKEN === "string" &&
  typeof p.user.REFRESH_TOKEN === "string";

const isFresh = (p) => Date.now() - p.ts <= BRIDGE_TTL_MS;

/**
 * Attempts cookie transport. Returns "ok" | "expired" | "invalid" | "absent".
 */
const consumeCookie = () => {
  const raw = getCookie(BRIDGE_COOKIE_NAME);
  if (!raw) return "absent";

  // Always best-effort delete the cookie, regardless of outcome.
  // The cookie was set by Sandbox on the shared parent domain (e.g. ".codeyoung.com").
  // To delete it correctly from SP we compute that same parent domain by
  // dropping the first subdomain label from our own hostname.
  // We also try a host-only delete (no domain) as a belt-and-suspenders fallback.
  const cleanup = () => {
    try {
      deleteCookie(BRIDGE_COOKIE_NAME); // host-only fallback
      const parts = window.location.hostname.split(".");
      if (parts.length >= 2) {
        // e.g. "studentportal.codeyoung.com" → ".codeyoung.com"
        const parentDomain = "." + parts.slice(1).join(".");
        deleteCookie(BRIDGE_COOKIE_NAME, { domain: parentDomain });
      }
    } catch {
      /* ignore */
    }
  };

  try {
    const payload = JSON.parse(raw);
    if (!isValidPayload(payload)) {
      cleanup();
      return "invalid";
    }
    if (!isFresh(payload)) {
      cleanup();
      return "expired";
    }
    writeUser(payload.user);
    // A new session from anywhere else (e.g. Epicenter impersonation) drops an old mark.
    if (payload.from === "sandbox") rememberSandboxReturn();
    else forgetSandboxReturn();
    cleanup();
    return "ok";
  } catch {
    cleanup();
    return "invalid";
  }
};

/**
 * Removes _spb from the URL hash without a page reload.
 */
const cleanHashParam = () => {
  const hash = window.location.hash;
  const qStart = hash.indexOf("?");
  if (qStart === -1) return;

  const path = hash.slice(1, qStart);
  const remaining = hash
    .slice(qStart + 1)
    .split("&")
    .filter((p) => !p.startsWith("_spb="))
    .join("&");

  window.history.replaceState(
    null,
    "",
    `#${remaining ? `${path}?${remaining}` : path}`,
  );
};

/**
 * Attempts URL-hash fallback. Returns "ok" | "expired" | "invalid" | "absent".
 */
const consumeHash = () => {
  const hash = window.location.hash;
  if (!hash.includes("_spb=")) return "absent";

  const qStart = hash.indexOf("?");
  if (qStart === -1) return "absent";

  const queryStr = hash.slice(qStart + 1);
  const match = queryStr.match(/(?:^|&)_spb=([^&]*)/);
  if (!match) return "absent";

  try {
    const payload = decrypt(decodeURIComponent(match[1]));
    if (!isValidPayload(payload)) {
      cleanHashParam();
      return "invalid";
    }
    if (!isFresh(payload)) {
      cleanHashParam();
      return "expired";
    }
    writeUser(payload.user);
    // A new session from anywhere else (e.g. Epicenter impersonation) drops an old mark.
    if (payload.from === "sandbox") rememberSandboxReturn();
    else forgetSandboxReturn();
    cleanHashParam();
    return "ok";
  } catch {
    cleanHashParam();
    return "invalid";
  }
};

/**
 * Public entry point. Idempotent and never throws.
 * Returns { consumed: boolean, transport: "cookie"|"hash"|null, outcome: string }
 */
export const consumeAuthBridge = () => {
  try {
    const cookieOutcome = consumeCookie();
    if (cookieOutcome === "ok") {
      // eslint-disable-next-line no-console
      console.info("[AuthBridge] cookie:ok");
      sessionStorage.setItem("_cy_from_sandbox", "1");
      return { consumed: true, transport: "cookie", outcome: "ok" };
    }
    if (cookieOutcome !== "absent") {
      // eslint-disable-next-line no-console
      console.warn(`[AuthBridge] cookie:${cookieOutcome}`);
      return { consumed: false, transport: "cookie", outcome: cookieOutcome };
    }

    const hashOutcome = consumeHash();
    if (hashOutcome === "ok") {
      // eslint-disable-next-line no-console
      console.info("[AuthBridge] hash:ok");
      sessionStorage.setItem("_cy_from_sandbox", "1");
      return { consumed: true, transport: "hash", outcome: "ok" };
    }
    if (hashOutcome !== "absent") {
      // eslint-disable-next-line no-console
      console.warn(`[AuthBridge] hash:${hashOutcome}`);
      return { consumed: false, transport: "hash", outcome: hashOutcome };
    }

    return { consumed: false, transport: null, outcome: "absent" };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[AuthBridge] unexpected error:", e);
    return { consumed: false, transport: null, outcome: "error" };
  }
};

export default consumeAuthBridge;
